
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  BarChart3, 
  Users, 
  Calendar, 
  Settings,
  MessageSquare,
  FileText,
  Stethoscope,
  Clipboard,
  File,
  MessageCircle
} from 'lucide-react';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import ROUTES from '@/routes-map';

const DoctorFooterNav = () => {
  const location = useLocation();
  const [openPopover, setOpenPopover] = useState<string | null>(null);
  
  const isActive = (path: string) => {
    return location.pathname.includes(path);
  };
  
  const handlePopoverOpenChange = (open: boolean, id: string) => {
    if (open) {
      setOpenPopover(id);
    } else if (openPopover === id) {
      setOpenPopover(null);
    }
  };
  
  // Menu structure for footer popover submenus
  const menus = {
    performance: [
      { label: 'Statistics', path: ROUTES.DOCTOR.STATISTICS, icon: BarChart3 },
      { label: 'Advertisements', path: ROUTES.DOCTOR.ADS, icon: null },
      { label: 'My Income', path: ROUTES.DOCTOR.FINANCE, icon: null },
      { label: 'Reviews', path: ROUTES.DOCTOR.REVIEWS, icon: null },
      { label: 'Stories', path: ROUTES.DOCTOR.STORIES, icon: null },
      { label: 'Before & After Gallery', path: ROUTES.DOCTOR.BEFORE_AFTER, icon: null }
    ],
    patients: [
      { label: 'My Patients', path: ROUTES.DOCTOR.PATIENTS, icon: Users },
      { label: 'Treatment Plans', path: ROUTES.DOCTOR.TREATMENT_PLANS, icon: Clipboard },
      { label: 'Treatment Sessions', path: ROUTES.DOCTOR.TREATMENT_SESSIONS, icon: Calendar },
      { label: 'Follow-up Reports', path: ROUTES.DOCTOR.FOLLOW_UP_REPORTS, icon: FileText },
      { label: 'Post-Treatment Reports', path: ROUTES.DOCTOR.POST_TREATMENT.ROOT, icon: Stethoscope },
      { label: 'Medical Documents', path: ROUTES.DOCTOR.MEDICAL_DOCUMENTS, icon: File }
    ],
    appointments: [
      { label: 'Appointments', path: ROUTES.DOCTOR.APPOINTMENTS, icon: Calendar },
      { label: 'Availability Settings', path: ROUTES.DOCTOR.AVAILABILITY, icon: null }
    ],
    settings: [
      { label: 'Profile Settings', path: ROUTES.DOCTOR.SETTINGS, icon: Settings },
      { label: 'Services & Pricing', path: ROUTES.DOCTOR.SERVICES, icon: null },
      { label: 'Professional Documents', path: '/doctor-area/documents', icon: null },
      { label: 'AI Assistant', path: '/doctor-area/ai-assistant', icon: null },
      { label: 'Help Center', path: '/doctor-area/faq-support', icon: MessageCircle }
    ]
  };
  
  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t z-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          {/* Dashboard - Direct link, no submenu */}
          <Link 
            to={ROUTES.DOCTOR.DASHBOARD} 
            className={cn(
              "flex flex-col items-center p-2",
              isActive('/dashboard') || location.pathname === '/' ? 'text-primary' : 'text-muted-foreground'
            )}
          >
            <Home className="h-5 w-5" />
            <span className="text-xs mt-1">Dashboard</span>
          </Link>
          
          {/* Performance with submenu */}
          <Popover 
            open={openPopover === 'performance'} 
            onOpenChange={(open) => handlePopoverOpenChange(open, 'performance')}
          >
            <PopoverTrigger asChild>
              <button
                className={cn(
                  "flex flex-col items-center p-2",
                  openPopover === 'performance' || 
                  isActive('/statistics') || 
                  isActive('/ads') || 
                  isActive('/finance') || 
                  isActive('/reviews') || 
                  isActive('/stories') || 
                  isActive('/before-after') 
                    ? 'text-primary' : 'text-muted-foreground'
                )}
              >
                <BarChart3 className="h-5 w-5" />
                <span className="text-xs mt-1">Performance</span>
              </button>
            </PopoverTrigger>
            <PopoverContent side="top" align="center" className="w-60 p-2">
              <div className="grid gap-1">
                {menus.performance.map((item, i) => (
                  <Link 
                    key={i} 
                    to={item.path}
                    className="flex items-center p-2 rounded-md hover:bg-accent text-sm"
                    onClick={() => setOpenPopover(null)}
                  >
                    {item.icon && <item.icon className="h-4 w-4 mr-2" />}
                    {item.label}
                  </Link>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          
          {/* My Patients - Center bigger icon */}
          <Popover 
            open={openPopover === 'patients'} 
            onOpenChange={(open) => handlePopoverOpenChange(open, 'patients')}
          >
            <PopoverTrigger asChild>
              <button
                className={cn(
                  "flex flex-col items-center p-2 -mt-5 bg-primary text-primary-foreground rounded-full w-16 h-16 shadow-md",
                  openPopover === 'patients' ? 'bg-primary/90' : ''
                )}
              >
                <Users className="h-7 w-7 mt-1" />
                <span className="text-xs mt-1">Patients</span>
              </button>
            </PopoverTrigger>
            <PopoverContent side="top" align="center" className="w-60 p-2">
              <div className="grid gap-1">
                {menus.patients.map((item, i) => (
                  <Link 
                    key={i} 
                    to={item.path}
                    className="flex items-center p-2 rounded-md hover:bg-accent text-sm"
                    onClick={() => setOpenPopover(null)}
                  >
                    {item.icon && <item.icon className="h-4 w-4 mr-2" />}
                    {item.label}
                  </Link>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          
          {/* Appointments with submenu */}
          <Popover 
            open={openPopover === 'appointments'} 
            onOpenChange={(open) => handlePopoverOpenChange(open, 'appointments')}
          >
            <PopoverTrigger asChild>
              <button
                className={cn(
                  "flex flex-col items-center p-2",
                  openPopover === 'appointments' || 
                  isActive('/appointments') || 
                  isActive('/availability') 
                    ? 'text-primary' : 'text-muted-foreground'
                )}
              >
                <Calendar className="h-5 w-5" />
                <span className="text-xs mt-1">Appointments</span>
              </button>
            </PopoverTrigger>
            <PopoverContent side="top" align="center" className="w-60 p-2">
              <div className="grid gap-1">
                {menus.appointments.map((item, i) => (
                  <Link 
                    key={i} 
                    to={item.path}
                    className="flex items-center p-2 rounded-md hover:bg-accent text-sm"
                    onClick={() => setOpenPopover(null)}
                  >
                    {item.icon && <item.icon className="h-4 w-4 mr-2" />}
                    {item.label}
                  </Link>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          
          {/* Settings with submenu */}
          <Popover 
            open={openPopover === 'settings'} 
            onOpenChange={(open) => handlePopoverOpenChange(open, 'settings')}
          >
            <PopoverTrigger asChild>
              <button
                className={cn(
                  "flex flex-col items-center p-2",
                  openPopover === 'settings' || 
                  isActive('/settings') || 
                  isActive('/documents') || 
                  isActive('/services') || 
                  isActive('/ai-assistant') || 
                  isActive('/faq') 
                    ? 'text-primary' : 'text-muted-foreground'
                )}
              >
                <Settings className="h-5 w-5" />
                <span className="text-xs mt-1">Settings</span>
              </button>
            </PopoverTrigger>
            <PopoverContent side="top" align="center" className="w-60 p-2 overflow-y-auto max-h-[60vh]">
              <div className="grid gap-1">
                {menus.settings.map((item, i) => (
                  <Link 
                    key={i} 
                    to={item.path}
                    className="flex items-center p-2 rounded-md hover:bg-accent text-sm"
                    onClick={() => setOpenPopover(null)}
                  >
                    {item.icon && <item.icon className="h-4 w-4 mr-2" />}
                    {item.label}
                  </Link>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </nav>
  );
};

export default DoctorFooterNav;
